import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getLocalisedNews } from "../../../api/news";
import PaginatedNewsList from "./PaginatedNewsList";
import NewsFilterBar from "./NewsFilterBar";
import ActiveFilterItem from "../ActiveFilterItem";
import { News } from "../../../compilers/news/News";
import Skeleton from "react-loading-skeleton";
import "../../../stylesheet/components/news/NewsSection.scss";

const numberOfSkeletons = 18;

const NewsCatalogue: React.FC = () => {
  const [news, setNews] = useState<News[]>([]);
  const [filteredNews, setFilteredNews] = useState<News[]>([]);
  const [textQuery, setTextQuery] = useState<string>("");
  const [orderBy, setOrderBy] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { ready, i18n } = useTranslation();

  useEffect(() => {
    setNews([]);
    if (ready) {
      getLocalisedNews(i18n.language).then((response: News[]) => {
        setNews(response);
        setFilteredNews(response);
      });
    }
  }, [i18n.language, ready]);

  /**
   * STATE MANIPULATION LOGIC
   * All functions which are handlers to alter the active filters and the pagination.
   **/
  const nextPage = () => {
    setCurrentPage(currentPage + 1);
  };

  const previousPage = () => {
    setCurrentPage(currentPage - 1);
  };

  // TEXT QUERY MANIPULATION FUNCTIONS
  const searchChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.value) {
      setTextQuery("");
    } else {
      setTextQuery(event.target.value.toLowerCase());
    }
  };

  const clearTextQuery = (clearText: string) => {
    setTextQuery(clearText);
  };

  const getSearchQueryPreview = () => {
    if (textQuery.length > 0) {
      return (
        <ActiveFilterItem
          key="search-query-preview"
          quoteTitle={true}
          itemId=""
          title={textQuery}
          removeFilter={clearTextQuery}
        />
      );
    }
  };

  // apply the text filter
  const filterOnText = (newsArticles: News[], textQuery: string): News[] => {
    if (textQuery.length < 1) {
      return newsArticles;
    }
    return newsArticles.filter((newsItem: News) => {
      if (typeof newsItem.data.body === "string") {
        return newsItem.data.title.toLowerCase().includes(textQuery);
      }
      return newsItem;
    });
  };

  // order the media based on published date
  const orderNewsByPublishedDate = (
    newsArticles: News[],
    orderByString: string
  ): News[] => {
    if (orderByString === "desc") {
      return newsArticles
        .sort((a: News, b: News) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return new Date(b.data.publishDate) - new Date(a.data.publishDate);
        })
        .slice(0);
    } else if (orderByString === "asc") {
      return newsArticles
        .sort((a: News, b: News) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return new Date(a.data.publishDate) - new Date(b.data.publishDate);
        })
        .slice(0);
    }
    return newsArticles;
  };

  useEffect(() => {
    let filterNewsArticles: News[] = news;

    filterNewsArticles = filterOnText(filterNewsArticles, textQuery);
    filterNewsArticles = orderNewsByPublishedDate(filterNewsArticles, orderBy);

    setFilteredNews(filterNewsArticles);
    setCurrentPage(1);
  }, [textQuery, orderBy]);

  const loadContentOrSkeleton = () => {
    if (!news || news.length < 1) {
      return (
        <div className="news-card-container mb-2">
          {[...Array(numberOfSkeletons)].map((i) => {
            return (
              <div key={i} className="news-card-wrapper card shadow-sm">
                <Skeleton width={100} count={2} />
                <Skeleton />
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <PaginatedNewsList
        news={filteredNews}
        currentPage={currentPage}
        nextPage={nextPage}
        previousPage={previousPage}
      />
    );
  };

  return (
    <div className="news-catalogue row position-relative mt-3">
      <NewsFilterBar
        textQuery={textQuery}
        setTextQuery={searchChangeHandler}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
      />
      <div className="d-flex flex-wrap align-items-center active-tags-container active-tags pt-2 mx-0">
        {getSearchQueryPreview()}
        {loadContentOrSkeleton()}
      </div>
    </div>
  );
};

export default NewsCatalogue;
